import { StorageManager } from 'src/app/common/storage-manager.class';
import { FormRequestTypes } from 'src/app/common/classes/form-request';
import type { FilteredValuesInterface } from 'src/app/common/components/form-builder/form-builder.component';
import {
    Form,
    FormField,
    FormSection,
    FormType
} from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import {
    InputType,
    RepeatableComputeContext,
} from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';

import { SectorModel } from 'src/app/models/form-common/sector.model';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ProductModel } from 'src/app/models/productos/product.model';
import { StockageModel } from 'src/app/models/stock/stokage.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';
import { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';

import { EntradasEnvironmentDefinition } from '../../environment-variables';
import { EntradasListsDefinition } from './list-variables';

export class EntradasFormDefinition {

    public formType!: FormType;

    public env: EntradasEnvironmentDefinition = new EntradasEnvironmentDefinition();

    public lists: EntradasListsDefinition = new EntradasListsDefinition();

    public formFields!: Form<StockageModel>;

    public fieldNames: FormField<any>[] = [];

    public fieldLabels: string[] = [];

    public cliente = StorageManager.getClient();

    public usuario = StorageManager.getUser();

    public model: StockageModel = {
        id_cliente: String(this.cliente?.id ?? ''),
        id_usuario: String(this.usuario?.id ?? ''),
        tipo: 'E',
        ids_productos: '',
        ids_tipos: '',
        array_unidades: '',
        array_lotes: '',
        array_marcas: '',
        array_envases: '',
        array_lotes_conselleria: '',
        unidades_medida: '',
        id_variedad: '',
        tipo_semilla: '',
        fecha: '',
        unidades: 0
    };

    constructor(
        type: FormRequestTypes,
        model: StockageModel,
        table: (string[] | string[][])[] | null
    ) {
        this.formType = +type;
        this.model = { ...this.model, ...model };

        if (this.env.forcesPlantType && !this.model.tipo_dato) {
            this.model.tipo_dato = 'Semilla';
        }

        this.formFields = {
            name: 'stock-entradas',
            header: 'Registrar movimiento',
            type: this.formType,
            sections: this.getFormSections(),
            table: {
                title: '',
                showIndex: false,
                data: table?.[0],
                style: table?.[1]
            },
            uploadButton: {
                isEnabled: false,
                label: ''
            },
            signed: false
        };
    }

    public getFormSections(): FormSection<any>[] {
        return [
            {
                name: 'Datos generales',
                fields: this.getGeneralFields(),
                width: 12,
                isVisible: true,
                order: 1
            },
            {
                name: 'Líneas',
                fields: this.getLineFields(),
                width: 12,
                isVisible: true,
                order: 2
            },
            {
                name: '',
                fields: this.getObservationFields(),
                width: 12,
                isVisible: true,
                order: 3
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 4
            }
        ];
    }

    public refreshProductosList(): void {
        const target = this.lists.productos;
        const tipoDato = String(this.model.tipo_dato ?? '');

        if (this.env.appName === 'detorres') {
            const base = this.lists.productosAll.values ?? [];

            target.values = base.map((item) => {
                const value = item.value as ProductModel | null;

                if (!value) {
                    return item;
                }

                return {
                    ...item,
                    label: `${value.nombre} (${value.tipo})`
                };
            });

            target.filtered = [...target.values];
            return;
        }

        if (tipoDato === 'Semilla' || tipoDato === 'Planta') {
            target.values = [...this.lists.cultivosAll.values];
            target.filtered = [...this.lists.cultivosAll.filtered];
            return;
        }

        const productTypeMap: Record<string, string> = {
            Fitosanitario: 'fitosanitario',
            Fertilizante: 'fertilizante',
            'Ganadería': 'ganaderia',
            Otros: 'otros',
            Bio: 'bio', 
        };

        const requiredType = productTypeMap[tipoDato];

        if (!requiredType) {
            target.values = [];
            target.filtered = [];
            return;
        }

        const values = this.lists.productosAll.values.filter((item) => {
            const value = item.value as ProductModel | null;

            return value?.tipo === requiredType;
        });

        const filtered = this.lists.productosAll.filtered.filter((item) => {
            const value = item.value as ProductModel | null;

            return value?.tipo === requiredType;
        });

        target.values = [...values];
        target.filtered = [...filtered];
    }

    private getGeneralFields(): FormField<any>[] {
        const generalFields: FormField<any>[] = [
            {
                name: 'id_finca',
                vinculatedFields: undefined,
                label: this.env.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.fincas,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'fecha',
                vinculatedFields: undefined,
                label: 'Fecha',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 2
            },
            {
                name: 'tipo',
                vinculatedFields: undefined,
                label: 'Tipo',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.tipoEntrada,
                    defaultValue: 'E'
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: 'motivo',
                vinculatedFields: undefined,
                label: 'Motivo',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.env.appName === 'covidai'
                        ? this.lists.motivosCovidai
                        : this.lists.motivos
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockMotivo
                },
                order: 4
            },
            {
                name: 'tipo_dato',
                vinculatedFields: ['ids_productos'],
                label: this.env.appName === 'detorres' ? 'Tipos detectados' : 'Tipo de producto',
                input: {
                    type: this.env.appName === 'detorres' ? InputType.HIDDEN : InputType.DROPDOWN,
                    isEditable: this.env.appName !== 'detorres',
                    data: this.lists.tiposDeDato,
                    defaultValue: this.env.forcesPlantType ? 'Semilla' : undefined
                },
                updateValue: (): string | undefined => {
                    if (this.env.forcesPlantType) {
                        return 'Semilla';
                    }

                    if (this.env.appName === 'detorres') {
                        return this.computeDetorresTipoDato();
                    }

                    return undefined;
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: this.env.showStockTipoDeDato,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockTipoDeDato &&
                        this.env.appName !== 'viverosblanco' &&
                        this.env.appName !== 'detorres'
                },
                order: 5
            },
            {
                name: 'id_comercio',
                vinculatedFields: ['tipo'],
                label: this.getCompanyFieldLabel(),
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.comercios,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValuesInterface<Compradores>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            if (this.env.appName === 'covidai') {
                                return true;
                            }

                            const tipo = String(this.model.tipo ?? 'E');
                            const companyType = String(it.value.tipo ?? '');

                            if (tipo === 'S') {
                                return companyType === 'Comercio' || companyType === 'Cliente';
                            }

                            return companyType === 'Proveedor';
                        },
                        swap: (field: FormField<any>): void => {
                            field.label = this.getCompanyFieldLabel();
                        }
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockProveedor && this.env.showStockProveedorClienteDropdown
                },
                order: 6
            },
            {
                name: 'proveedor',
                vinculatedFields: ['id_comercio'],
                label: 'Proveedor',
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                },
                updateValue: (): string | undefined => {
                    if (!this.env.showStockProveedorClienteDropdown) {
                        return undefined;
                    }

                    const selected = this.lists.comercios.selected as Compradores | null;

                    return selected?.razon_social ?? '';
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockProveedor && !this.env.showStockProveedorClienteDropdown
                },
                order: 7
            },
            {
                name: 'cif_proveedor',
                vinculatedFields: undefined,
                label: 'NIF/CIF',
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockCifProveedor
                },
                order: 8
            },
            {
                name: 'albaran',
                vinculatedFields: undefined,
                label: 'Albarán/Factura',
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 9
            },
            {
                name: 'id_sector',
                vinculatedFields: ['id_finca'],
                label: this.env.sectorName,
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.sectores,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValuesInterface<SectorModel>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            const finca = this.lists.fincas.selected as FincasModel | null;

                            return it.value.id_finca === finca?.id;
                        }
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'covidai' || this.env.appName === 'sendra'
                },
                order: 10
            },
            {
                name: 'ubicacion',
                vinculatedFields: ['id_finca', 'id_sector'],
                label: this.env.parcelaName + ' de origen',
                input: this.buildUbicacionInput(),
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockUbicacion ||
                        this.env.appName === 'covidai' ||
                        this.env.appName === 'bravosol'
                },
                order: 11
            },
            {
                name: 'ubicacion_destino',
                vinculatedFields: ['id_finca'],
                label: this.env.parcelaName + ' de destino',
                input: this.buildUbicacionDestinoInput(),
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: 
                        this.env.showStockUbicacionDestino ||
                        this.env.appName === 'covidai' ||
                        this.env.appName === 'bravosol' ||
                        this.env.appName === 'viverosblanco'
                },
                order: 12
            },
            {
                name: 'fecha_injerto',
                vinculatedFields: undefined,
                label: 'Fecha injerto',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'viverosblanco'
                },
                order: 13
            },
            {
                name: 'articulo',
                vinculatedFields: undefined,
                label: 'Artículo',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.articulos,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'cod_producto'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'bravosol'
                },
                order: 14
            },
            {
                name: 'unidades_medida',
                vinculatedFields: undefined,
                label: 'Unidades de medida',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.unidades
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showStockTipoDeDato &&
                        !this.env.showStockAdditionalFields &&
                        this.env.appName !== 'pyf' &&
                        this.env.applicationType !== 'viverogest'
                },
                order: 15
            }
        ];
        this.fieldNames = this.fieldNames.concat(generalFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(generalFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return generalFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLineFields(): FormField<any>[] {
        const lineFields: FormField<any>[] = [
            {
                name: 'lineas_group',
                vinculatedFields: ['tipo_dato'],
                label: '',
                input: {
                    type: InputType.REPEATABLE_GROUP,
                    isEditable: true,
                    repeatableGroupConfiguration: {
                        rows: this.env.maxRows,
                        addButtonLabel: 'Añadir línea',
                        bindings: this.getRepeatableBindings(),
                        serialization: {
                            separator: ';'
                        },
                        subFields: this.getRepeatableSubFields()
                    }
                },
                width: 12,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            }
        ];
        this.fieldNames = this.fieldNames.concat(lineFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(lineFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return lineFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getObservationFields(): FormField<any>[] {
        const observationFields: FormField<any>[] = [
            {
                name: 'observaciones',
                vinculatedFields: undefined,
                label: this.env.appName === 'armentia'
                    ? 'Observaciones (tratamiento)'
                    : 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        rows: 4,
                        cols: 30,
                        hasVoiceTranscription: false,
                        canTakePhoto: false,
                        photoField: undefined
                    }
                },
                width: 12,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            }
        ];
        this.fieldNames = this.fieldNames.concat(observationFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(observationFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return observationFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields(): FormField<any>[] {
        const fields: FormField<any>[] = [
            this.hiddenField('id'),
            this.hiddenField('id_cliente', ['id_finca'], (): string => {
                const finca = this.lists.fincas.selected as FincasModel | null;

                return String(
                    finca?.id_cliente ??
                    this.model.id_cliente ??
                    this.cliente?.id ??
                    ''
                );
            }),
            this.hiddenField('id_usuario', ['id_finca'], (): string => {
                const finca = this.lists.fincas.selected as FincasModel | null;

                return String(
                    finca?.id_usuario ??
                    this.model.id_usuario ??
                    this.usuario?.id ??
                    ''
                );
            }),
            this.hiddenField('ids_productos'),
            this.hiddenField('ids_tipos', ['ids_productos'], (): string => {
                if (!this.env.showStockTipoDeDato) {
                    return '';
                }

                return String(this.model.ids_productos ?? '');
            }),
            this.hiddenField('array_unidades'),
            this.hiddenField('array_lotes'),
            this.hiddenField('array_marcas'),
            this.hiddenField('array_envases'),
            this.hiddenField('array_lotes_conselleria'),
            this.hiddenField('unidades_medida'),
            this.hiddenField('id_variedad'),
            this.hiddenField('tipo_semilla'),
            this.hiddenField('cod_mb'),
            this.hiddenField('categoria_mfr'),
            this.hiddenField('formato'),
            this.hiddenField('uds_bandeja'),
            this.hiddenField('uds_producidas'),
        ];

        if (this.env.appName === 'covidai') {
            fields.push(
                this.hiddenField('lote1', ['array_lotes'], (): string => this.firstToken(this.model.array_lotes)),
                this.hiddenField('lote1_conselleria', ['array_lotes_conselleria'], (): string =>
                    this.firstToken(this.model.array_lotes_conselleria)
                )
            );
        }

        return fields;
    }

    private getRepeatableBindings(): Record<string, string> {
        const bindings: Record<string, string> = {
            producto: 'ids_productos',
            formato: 'formato',
            udsBandeja: 'uds_bandeja',
            cantidad: 'array_unidades',
            udsProducidas: 'uds_producidas',
            lote: 'array_lotes',
            marcaComercial: 'array_marcas',
            envase: 'array_envases',
            loteConselleria: 'array_lotes_conselleria',
            variedad: 'id_variedad',
            tipoSemilla: 'tipo_semilla',
            codMb: 'cod_mb',
            categoriaMfr: 'categoria_mfr',
        };

        if (this.env.showStockAdditionalFields) {
            bindings['unidadMedida'] = 'unidades_medida';
        }

        return bindings;
    }

    private getRepeatableSubFields() {
        const isPlantData = (): boolean => String(this.model.tipo_dato ?? '') === 'Planta' || String(this.model.tipo_dato ?? '') === 'Semilla';

        return [
            {
                key: 'producto',
                label: this.getProductFieldLabel(),
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.productos,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                }
            },
            {
                key: 'variedad',
                label: 'Variedad',
                isVisible: (): boolean => (
                    (this.env.appName === 'pyf' || this.env.appName === 'sendra' || this.env.appName === 'viverosmuzale') &&
                    (String(this.model.tipo_dato ?? '') === 'Planta' || String(this.model.tipo_dato ?? '') === 'Semilla')
                ),
                getData: (ctx: any) => {
                    const rawProducto = ctx.row['producto'];

                    const producto =
                        rawProducto != null && typeof rawProducto === 'object'
                            ? rawProducto as Record<string, unknown>
                            : this.resolveRepeatableProducto(rawProducto);

                    if (!producto) {
                        return {
                            values: [],
                            filtered: [],
                            selected: null
                        };
                    }

                    const cultivoSelect = String(producto['cultivo'] ?? '').trim();

                    if (cultivoSelect === '') {
                        return {
                            values: [],
                            filtered: [],
                            selected: null
                        };
                    }

                    const opciones = this.lists.auxCultivos.filtered
                        .filter((item) => String(item.label ?? '').trim() === cultivoSelect)
                        .map((item) => {
                            const value = item.value as VisitasVariedadMesModel | null;

                            return {
                                label: String(value?.variedad ?? ''),
                                value: String(value?.id ?? '')
                            };
                        })
                        .filter((item) => item.label !== '' && item.value !== '');

                    return {
                        values: [...opciones],
                        filtered: [...opciones],
                        selected: null
                    };
                },
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.auxCultivos
                }
            },
            {
                key: 'formato',
                label: 'Formato',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.formatosViverogest,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'codigo'
                    }
                }
            },
            {
                key: 'udsBandeja',
                label: 'Uds. bandeja',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                computeValue: (ctx: RepeatableComputeContext): string => {
                    const formatoRaw = String(ctx.model.get('formato')?.value ?? '').trim();
                    const partes = formatoRaw.split(';');
                    const codigo = (partes[ctx.rowIndex] ?? '').trim();

                    const uds = this.lists.formatosViverogest.filtered.find(
                        (item) => item.value?.codigo === codigo
                    )?.value?.uds_bandeja ?? null;

                    return uds != null ? String(uds) : '';
                },
                input: {
                    type: InputType.NUMERIC,
                    isEditable: false
                }
            },
            {
                key: 'tipoSemilla',
                label: 'Categoría',
                isVisible: (): boolean => this.env.appName === 'pyf' && isPlantData(),
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.listaSemilla
                }
            },
            {
                key: 'cantidad',
                label: this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta'
                    ? 'Uds de bandejas'
                    : 'Cantidad',
                input: {
                    type: InputType.NUMERIC,
                    isEditable: true
                }
            },
            {
                key: 'udsProducidas',
                label: 'Uds. producidas',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                computeValue: (ctx: RepeatableComputeContext): string => {
                    const formatoRaw = String(ctx.model.get('formato')?.value ?? '').trim();
                    const partes = formatoRaw.split(';');
                    const codigo = (partes[ctx.rowIndex] ?? '').trim();

                    const cantidad = Number(ctx.rows[ctx.rowIndex]?.['cantidad'] ?? 0);
                    if (cantidad === 0) return '';

                    const uds = this.lists.formatosViverogest.filtered.find(
                        (item) => item.value?.codigo === codigo
                    )?.value?.uds_bandeja ?? null;

                    if (uds == null) return '';

                    return String(cantidad * uds);
                },
                input: {
                    type: InputType.NUMERIC,
                    isEditable: false
                }
            },
            {
                key: 'marcaComercial',
                label: 'Marca comercial',
                isVisible: (): boolean => this.env.showStockMarcaComercial,
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'envase',
                label: 'Envase',
                isVisible: (): boolean => this.env.showStockEnvase,
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'lote',
                label: 'Lote propio',
                isVisible: (): boolean => this.env.showStockLotePropio,
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'codMb',
                label: 'Cod. MB/CPM',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'categoriaMfr',
                label: 'Categoría MFR',
                isVisible: (): boolean => this.env.applicationType === 'viverogest' && String(this.model.tipo_dato ?? '') === 'Planta',
                input: {
                    type: InputType.SWITCH,
                    isEditable: true
                }
            },
            {
                key: 'loteConselleria',
                label: 'Lote conselleria',
                isVisible: (): boolean => this.env.usesConselleriaLot,
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                }
            },
            {
                key: 'unidadMedida',
                label: 'Unidad medida',
                isVisible: (): boolean => this.env.showStockAdditionalFields && this.env.applicationType !== 'viverogest',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.unidades,
                }
            }
        ];
    }

    private getCompanyFieldLabel(): string {
        return String(this.model.tipo ?? 'E') === 'S'
            ? this.env.clienteName
            : 'Proveedor';
    }

    private getProductFieldLabel(): string {
        if (this.env.appName === 'detorres') {
            return 'Producto';
        }

        if (!this.env.showStockTipoDeDato) {
            if (this.env.appName === 'vginer') {
                return 'Fertilizante';
            }

            if (this.env.appName === 'covidai') {
                return 'Variedad';
            }

            return 'Producto/Marca comercial';
        }

        if (this.env.appName === 'momparler') {
            return 'Producto o planta';
        }

        if (this.env.appName === 'viverosblanco' || this.env.appName === 'covidai') {
            return 'Producto';
        }

        return String(this.model.tipo_dato || 'Producto');
    }

    private computeDetorresTipoDato(): string {
        const ids = String(this.model.ids_productos ?? '')
            .split(';')
            .map((id) => id.trim())
            .filter((id) => id !== '');

        if (ids.length === 0) {
            return '';
        }

        const values = this.lists.productosAll.values as Array<{ value: ProductModel | null; label: string }>;

        const types = ids
            .map((id) => {
                const match = values.find((item) => String(item.value?.id ?? '') === id);
                const tipo = String(match?.value?.tipo ?? '');

                if (tipo === '') {
                    return '';
                }

                return tipo.charAt(0).toUpperCase() + tipo.slice(1).toLowerCase();
            })
            .filter((tipo) => tipo !== '');

        return types.join(';');
    }

    private resolveRepeatableProducto(rawProducto: unknown): Record<string, unknown> | null {
        const productoId = String(rawProducto ?? '').trim();

        if (productoId === '') {
            return null;
        }

        const map = new Map<string, Record<string, unknown>>();

        for (const item of this.lists.productos.values ?? []) {
            const value = item.value as Record<string, unknown> | null;

            if (value?.['id'] != null) {
                map.set(String(value['id']), value);
            }
        }

        for (const item of this.lists.productos.filtered ?? []) {
            const value = item.value as Record<string, unknown> | null;

            if (value?.['id'] != null) {
                map.set(String(value['id']), value);
            }
        }

        return map.get(productoId) ?? null;
    }

    private buildUbicacionInput(): FormField<any>['input'] {
        const usesParcelDropdown = this.env.appName === 'covidai' || this.env.appName === 'bravosol';

        if (usesParcelDropdown) {
            return {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.parcelas,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    filter: (it: FilteredValuesInterface<ParcelasModel>): boolean => {
                        if (it.value == null) {
                            return true;
                        }

                        const finca = this.lists.fincas.selected as FincasModel | null;
                        const sector = this.lists.sectores.selected as SectorModel | null;

                        if (this.env.appName === 'covidai' && sector != null) {
                            return it.value.id_sector === sector.id;
                        }

                        return it.value.id_finca === finca?.id;
                    }
                }
            };
        }

        return {
            type: InputType.DROPDOWN_SEARCH,
            isEditable: true,
            data: this.env.appName === 'sendra' ? this.lists.parcelas :
                this.env.appName === 'viverosblanco'
                    ? this.lists.ubicacionesViveros
                    : this.lists.ubicaciones,
            dropdownConfiguration: {
                valuePrimaryKey: 'id'
            }
        };
    }

    private buildUbicacionDestinoInput(): FormField<any>['input'] {
        const usesParcelDropdown = this.env.appName === 'covidai' || this.env.appName === 'bravosol';

        if (usesParcelDropdown) {
            return {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.parcelasDestino,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    filter: (it: FilteredValuesInterface<ParcelasModel>): boolean => {
                        if (it.value == null) {
                            return true;
                        }

                        const finca = this.lists.fincas.selected as FincasModel | null;

                        return it.value.id_finca === finca?.id;
                    }
                }
            };
        }

        return {
            type: InputType.DROPDOWN_SEARCH,
            isEditable: true,
            data: this.env.appName === 'sendra' ? this.lists.parcelasDestino : this.lists.ubicacionesViveros,
            dropdownConfiguration: {
                valuePrimaryKey: 'id'
            }
        };
    }

    private firstToken(value: string | undefined): string {
        return String(value ?? '').split(';')[0] ?? '';
    }

    private hiddenField(
        name: string,
        vinculatedFields?: string[],
        updateValue?: FormField<any>['updateValue']
    ): FormField<any> {
        const field: FormField<any> = {
            name,
            vinculatedFields,
            label: '',
            input: {
                type: InputType.HIDDEN,
                isEditable: true
            },
            width: 1,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true
            },
            order: 999
        };

        if (updateValue) {
            field.updateValue = updateValue;
        }

        return field;
    }


    public refreshLineFieldLabels(): void {
        this.formFields = {
            ...this.formFields,
            sections: this.formFields.sections.map((section) => {
                if (section.name !== 'Líneas') {
                    return section;
                }

                return {
                    ...section,
                    fields: section.fields.map((field) => {
                        if (field.name !== 'lineas_group' || !field.input.repeatableGroupConfiguration) {
                            return field;
                        }

                        return {
                            ...field,
                            input: {
                                ...field.input,
                                repeatableGroupConfiguration: {
                                    ...field.input.repeatableGroupConfiguration,
                                    subFields: field.input.repeatableGroupConfiguration.subFields.map((sf) => {
                                        if (sf.key === 'producto') {
                                            return {
                                                ...sf,
                                                label: this.getProductFieldLabel()
                                            };
                                        }

                                        if (sf.key === 'cantidad') {
                                            return {
                                                ...sf,
                                                label: this.env.applicationType === 'viverogest' &&
                                                    String(this.model.tipo_dato ?? '') === 'Planta'
                                                    ? 'Uds de bandejas'
                                                    : 'Cantidad'
                                            };
                                        }

                                        return sf;
                                    })
                                }
                            }
                        };
                    })
                };
            })
        };
    }

    public updateFincaLabel(isMov: boolean): void {
        this.formFields = {
            ...this.formFields,
            sections: this.formFields.sections.map((section) => ({
                ...section,
                fields: section.fields.map((field) => {
                    if (field.name !== 'id_finca') {
                        return field;
                    }
                    return {
                        ...field,
                        label: isMov ? 'Invernadero Destino' : this.env.fincaName
                    };
                })
            }))
        };
    }

    public updateFincaRequired(isRequired: boolean): void {
        this.formFields = {
            ...this.formFields,
            sections: this.formFields.sections.map((section) => ({
                ...section,
                fields: section.fields.map((field) => {
                    if (field.name !== 'id_finca') {
                        return field;
                    }
                    return {
                        ...field,
                        conditions: {
                            ...field.conditions,
                            isRequired
                        }
                    };
                })
            }))
        };
    }
}